import { useCallback, useMemo } from 'react';
import {
    keepPreviousData,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
    type InfiniteData,
    type QueryKey
} from '@tanstack/react-query';
import {
    downloadIteroAsset,
    fetchIteroOrder,
    fetchIteroRx,
    listIteroOrders,
    searchIteroPatients,
    type IteroAssetResult,
    type IteroAssetType,
    type IteroOrder,
    type IteroOrdersFilterParams,
    type IteroOrdersParams,
    type IteroPatientsFilterParams,
    type IteroPatientsParams
} from '../services/iteroBrowse';
import { firstPageOnly } from '../components/accounts/practice/itero-browser/browsePaging';
import { nextOrdersOffset } from '../components/accounts/practice/itero-browser/orderQuery';
import {
    nextPatientsOffset,
    patientLookupParams
} from '../components/accounts/practice/itero-browser/patientQuery';
import {
    ITERO_ORDERS_KEY,
    ITERO_ORDERS_PAGE_KEY,
    ITERO_ORDER_KEY,
    ITERO_PATIENTS_INFINITE_KEY,
    ITERO_PATIENTS_KEY,
    ITERO_PATIENT_ORDERS_KEY,
    ITERO_RX_KEY
} from './queryKeys';
import { useUnmountAbortSignal } from './useUnmountAbortSignal';

export interface IteroBrowseScope {
    practiceId: string;
    clinicId: number;
}

const ONE_MINUTE_MS = 60_000;
const FIVE_MINUTES_MS = 5 * ONE_MINUTE_MS;
const FIRST_OFFSET = 0;

// iTero asks integrators not to poll: no focus/reconnect refetches, and no automatic retries
// (every view has a Retry button). A short gcTime keeps patient records in memory only briefly.
export const BROWSE_QUERY_OPTIONS = {
    staleTime: ONE_MINUTE_MS,
    gcTime: FIVE_MINUTES_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false
} as const;

export function useIteroPatients(
    scope: IteroBrowseScope,
    params: IteroPatientsParams,
    enabled: boolean
) {
    return useQuery({
        queryKey: ITERO_PATIENTS_KEY(scope.practiceId, scope.clinicId, params),
        queryFn: () => searchIteroPatients(scope.practiceId, params),
        placeholderData: keepPreviousData,
        enabled,
        ...BROWSE_QUERY_OPTIONS
    });
}

export function useIteroPatientsInfinite(
    scope: IteroBrowseScope,
    filters: IteroPatientsFilterParams,
    enabled: boolean
) {
    return useInfiniteQuery({
        queryKey: ITERO_PATIENTS_INFINITE_KEY(scope.practiceId, scope.clinicId, filters),
        queryFn: ({ pageParam }) =>
            searchIteroPatients(scope.practiceId, { ...filters, offset: pageParam }),
        initialPageParam: FIRST_OFFSET,
        getNextPageParam: nextPatientsOffset,
        placeholderData: keepPreviousData,
        enabled,
        ...BROWSE_QUERY_OPTIONS
    });
}

export function useIteroOrders(
    scope: IteroBrowseScope,
    filters: IteroOrdersFilterParams,
    enabled: boolean
) {
    return useInfiniteQuery({
        queryKey: ITERO_ORDERS_KEY(scope.practiceId, scope.clinicId, filters),
        queryFn: ({ pageParam }) =>
            listIteroOrders(scope.practiceId, { ...filters, offset: pageParam }),
        initialPageParam: FIRST_OFFSET,
        getNextPageParam: nextOrdersOffset,
        placeholderData: keepPreviousData,
        enabled,
        ...BROWSE_QUERY_OPTIONS
    });
}

export function useIteroOrdersPage(
    scope: IteroBrowseScope,
    params: IteroOrdersParams,
    enabled: boolean
) {
    return useQuery({
        queryKey: ITERO_ORDERS_PAGE_KEY(scope.practiceId, scope.clinicId, params),
        queryFn: () => listIteroOrders(scope.practiceId, params),
        placeholderData: keepPreviousData,
        enabled,
        ...BROWSE_QUERY_OPTIONS
    });
}

// Trims a scrolled list's cache to its first page, so going back to infinite scroll starts at the top.
function useScrollReset<TFilters>(keyFor: (filters: TFilters) => QueryKey) {
    const queryClient = useQueryClient();
    return useCallback(
        (filters: TFilters) =>
            queryClient.setQueryData<InfiniteData<unknown, unknown>>(
                keyFor(filters),
                firstPageOnly
            ),
        [queryClient, keyFor]
    );
}

export function useIteroPatientsScrollReset(scope: IteroBrowseScope) {
    const { practiceId, clinicId } = scope;
    const keyFor = useCallback(
        (filters: IteroPatientsFilterParams) =>
            ITERO_PATIENTS_INFINITE_KEY(practiceId, clinicId, filters),
        [practiceId, clinicId]
    );
    return useScrollReset(keyFor);
}

export function useIteroOrdersScrollReset(scope: IteroBrowseScope) {
    const { practiceId, clinicId } = scope;
    const keyFor = useCallback(
        (filters: IteroOrdersFilterParams) => ITERO_ORDERS_KEY(practiceId, clinicId, filters),
        [practiceId, clinicId]
    );
    return useScrollReset(keyFor);
}

export function useIteroPatientOrders(scope: IteroBrowseScope, params: IteroOrdersFilterParams) {
    const chartNumber = params.chartNumber ?? '';
    return useQuery({
        queryKey: ITERO_PATIENT_ORDERS_KEY(scope.practiceId, scope.clinicId, chartNumber),
        queryFn: () => listIteroOrders(scope.practiceId, { ...params, offset: FIRST_OFFSET }),
        enabled: chartNumber.length > 0,
        ...BROWSE_QUERY_OPTIONS
    });
}

// Fetching the single order (not just reusing the list row) records the view in the audit log.
// Without a list row (an order restored from the URL) there is no placeholder; a null rxId fetches nothing.
export function useIteroOrder(
    scope: IteroBrowseScope,
    rxId: string | null,
    listOrder?: IteroOrder
) {
    return useQuery({
        queryKey: ITERO_ORDER_KEY(scope.practiceId, scope.clinicId, rxId ?? ''),
        queryFn: () => fetchIteroOrder(scope.practiceId, rxId ?? ''),
        placeholderData: listOrder,
        enabled: rxId !== null,
        ...BROWSE_QUERY_OPTIONS
    });
}

// No keepPreviousData: a restore must only ever match against the chart it asked for.
export function useIteroPatientLookup(scope: IteroBrowseScope, chartNumber: string | null) {
    const params = useMemo(() => patientLookupParams(chartNumber ?? ''), [chartNumber]);
    return useQuery({
        queryKey: ITERO_PATIENTS_KEY(scope.practiceId, scope.clinicId, params),
        queryFn: () => searchIteroPatients(scope.practiceId, params),
        enabled: params.chartNumber !== '',
        ...BROWSE_QUERY_OPTIONS
    });
}

export function useIteroRx(scope: IteroBrowseScope, rxId: string, enabled: boolean) {
    return useQuery({
        queryKey: ITERO_RX_KEY(scope.practiceId, scope.clinicId, rxId),
        queryFn: () => fetchIteroRx(scope.practiceId, rxId),
        enabled,
        ...BROWSE_QUERY_OPTIONS
    });
}

export function useIteroAssetDownload(
    scope: IteroBrowseScope,
    rxId: string,
    reuse?: (assetType: IteroAssetType) => IteroAssetResult | null
) {
    // A download outliving its order view could never be saved, so closing the view aborts it.
    const nextSignal = useUnmountAbortSignal();
    return useMutation({
        mutationFn: async (assetType: IteroAssetType) =>
            reuse?.(assetType) ??
            downloadIteroAsset(scope.practiceId, rxId, assetType, { signal: nextSignal() })
    });
}
